import { Box, Grid } from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { Check } from "@material-ui/icons";
import { Championship, Collections, RaceControl, StatusRaceControl } from "api";
import { useSnackbar } from "notistack";
import React, { useCallback, useContext, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { useParams } from "react-router";
import {
    ConfirmationDialog,
    FormButton,
    LoggedUserContext,
} from "ui/componentes";
import { IncidentList } from "ui/views/ChampionshipForm/RoundEdit/IncidentList";
import { IncidentListAfterRace } from "ui/views/RaceControlView";

interface RaceControlProps {
    championship: Championship;
    raceControl: RaceControl;
}

const RaceControlForm: React.FC<RaceControlProps> = ({
    raceControl,
    championship,
}) => {
    const firestore = useFirestore();
    const { isAdmin } = useContext(LoggedUserContext);
    const { enqueueSnackbar } = useSnackbar();
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showReopenConfirmation, setShowReopenConfirmation] = useState(false);
    const [loading, setLoading] = useState(false);
    const { afterRace } = useParams<{ afterRace }>();

    const finishRaceControl = useCallback(() => {
        setLoading(true);
        setShowConfirmation(false);

        firestore
            .collection(Collections.RACES_CONTROL)
            .doc(raceControl.id)
            .update({ status: StatusRaceControl.FINISHED })
            .then(() => {
                enqueueSnackbar(
                    `Moderação finalizada para a bateria ${raceControl.raceNumber}`,
                    { variant: "success" }
                );
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                enqueueSnackbar(
                    `Erro ao finalizar a bateria ${raceControl.raceNumber}`,
                    { variant: "error" }
                );
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raceControl.id, raceControl.raceNumber]);

    const reopenRaceControl = useCallback(() => {
        setLoading(true);
        setShowReopenConfirmation(false);

        firestore
            .collection(Collections.RACES_CONTROL)
            .doc(raceControl.id)
            .update({ status: StatusRaceControl.IN_PROGRESS })
            .then(() => {
                enqueueSnackbar(
                    `Moderação reaberta para a bateria ${raceControl.raceNumber}`,
                    { variant: "success" }
                );
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                enqueueSnackbar(
                    `Erro ao reabrir a bateria ${raceControl.raceNumber}`,
                    { variant: "error" }
                );
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [raceControl.id, raceControl.raceNumber]);

    return (
        <>
            <ConfirmationDialog
                showDialog={showConfirmation}
                message={`Ao encerrar a bateria, não será mais possível adicionar incidentes.`}
                title={`Deseja encerrar a moderação para a bateria ${raceControl.raceNumber}?`}
                onCancel={() => setShowConfirmation(false)}
                onOk={finishRaceControl}
            />
            <ConfirmationDialog
                showDialog={showReopenConfirmation}
                title={`Deseja reabrir a moderação para a bateria ${raceControl.raceNumber}?`}
                onCancel={() => setShowReopenConfirmation(false)}
                onOk={reopenRaceControl}
            />
            <Box marginTop={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12} hidden={afterRace === "true"}>
                        <IncidentList
                            championship={championship}
                            raceControl={raceControl}
                        />
                    </Grid>
                    <Grid item xs={12} hidden={afterRace === "false"}>
                        <IncidentListAfterRace
                            championship={championship}
                            raceControl={raceControl}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" width="100%">
                            {raceControl.status !==
                                StatusRaceControl.FINISHED && (
                                <FormButton
                                    startIcon={<Check />}
                                    fullWidth={false}
                                    loading={loading}
                                    onClick={() => setShowConfirmation(true)}
                                    style={{
                                        backgroundColor: green[500],
                                        color: "white",
                                    }}
                                >
                                    {`Finalizar bateria ${
                                        raceControl.raceNumber || ""
                                    }`}
                                </FormButton>
                            )}
                            {isAdmin &&
                                raceControl.status ===
                                    StatusRaceControl.FINISHED && (
                                    <FormButton
                                        fullWidth={false}
                                        loading={loading}
                                        color="primary"
                                        onClick={() =>
                                            setShowReopenConfirmation(true)
                                        }
                                    >
                                        {`Reabrir bateria ${
                                            raceControl.raceNumber || ""
                                        }`}
                                    </FormButton>
                                )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export { RaceControlForm };
